// MJ1: Min jakt öppnar en lista över markerna man är med i (i stället för kartan i helskärm).
// En låg rad per mark: rund markbild, namn, senaste bilderna ur albumet, röd bubbla med
// olästa chattmeddelanden och nästa jakt i mindre text. Tryck öppnar marken.
import React from 'react';
import { Home } from 'lucide-react';
import { api, isVideoUrl, resolveUrl, type Mark, type MarkAlbumImage, type MarkCalendarEvent } from '../../api';
import { RADIUS, useTheme } from '../../theme';
import { Icon } from '../../components/ui';
import { markImg, GREEN_PLACEHOLDER } from '../../components';
import { CHATS_CHANGED_EVENT, UnreadBadge } from '../../components/chat/ChatList';
import { shortDate, timeOf } from './parts';

/** Så många av de senaste albumbilderna visas på raden. */
export const LIST_IMAGES = 3;

const isHuntEvent = (e: MarkCalendarEvent) => ['hunt', 'jakt'].includes((e.eventType || '').toLowerCase());

/** Nästa jakt på marken (inte avslutad, inte passerad). */
export function nextHunt(events: MarkCalendarEvent[], markId: string, now = Date.now()): MarkCalendarEvent | undefined {
  return events
    .filter(e => e.markId === markId && isHuntEvent(e) && e.status !== 'completed' && new Date(e.startTime).getTime() >= now)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())[0];
}

/** "Nästa jakt lör 8 nov 06:30" */
export function nextHuntLine(ev?: MarkCalendarEvent): string {
  if (!ev) return 'Ingen jakt planerad';
  const t = timeOf(ev.startTime);
  return `Nästa jakt ${shortDate(ev.startTime).toLowerCase()}${t ? ` ${t}` : ''}`;
}

/** De senaste bilderna först. */
export function latestImages(images: MarkAlbumImage[], n = LIST_IMAGES): MarkAlbumImage[] {
  // Videor (I2, jaktchatten) visas inte som miniatyrbilder i listan.
  return images.filter(i => !isVideoUrl(i.imageUrl)).sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))).slice(0, n);
}

/** Olästa per mark (CH1:s chattlista) och de senaste albumbilderna per mark. */
export function useMarkListExtras(marks: Mark[]) {
  const [unread, setUnread] = React.useState<Record<string, number>>({});
  const [images, setImages] = React.useState<Record<string, MarkAlbumImage[]>>({});
  const ids = marks.map(m => m.id).join(',');
  React.useEffect(() => {
    if (!ids) return;
    let alive = true;
    const load = () => api.myChats().then(cs => {
      if (!alive) return;
      const out: Record<string, number> = {};
      for (const c of cs) if (c.markId) out[c.markId] = (out[c.markId] ?? 0) + (c.unreadCount || 0);
      setUnread(out);
    }).catch(() => {});
    void load();
    window.addEventListener(CHATS_CHANGED_EVENT, load);
    return () => { alive = false; window.removeEventListener(CHATS_CHANGED_EVENT, load); };
  }, [ids]);
  React.useEffect(() => {
    if (!ids) return;
    let alive = true;
    // Albumet kräver behörighet (canViewAlbum); utan den blir raden bara utan bilder.
    ids.split(',').forEach(id => {
      api.markAlbumImages(id).then(imgs => { if (alive) setImages(prev => ({ ...prev, [id]: latestImages(imgs) })); }).catch(() => {});
    });
    return () => { alive = false; };
  }, [ids]);
  return { unread, images };
}

export function MarkListRow({ mark, unread, images, maxImages = LIST_IMAGES, next, role, isHome, first, onOpen, onToggleHome }: {
  mark: Mark; unread: number; images: MarkAlbumImage[]; maxImages?: number; next?: MarkCalendarEvent; role?: string;
  isHome: boolean; first?: boolean; onOpen: () => void; onToggleHome: () => void;
}) {
  const T = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'center', borderTop: first ? 'none' : `1px solid ${T.border}` }}>
      <button type="button" onClick={onOpen} aria-label={`Öppna ${mark.name}${unread ? `, ${unread} olästa` : ''}`} style={{
        flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 4px 12px 0', minHeight: 72,
        background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', color: T.text,
      }}>
        <span style={{ position: 'relative', flexShrink: 0, display: 'block' }}>
          <img src={markImg(mark)} alt="" style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', display: 'block', background: T.surface2 }}
            onError={e => { (e.target as HTMLImageElement).src = GREEN_PLACEHOLDER; }} />
          {unread > 0 && <UnreadBadge n={unread} style={{ position: 'absolute', top: -3, right: -5, border: `2px solid ${T.bg}`, boxSizing: 'content-box' }} />}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <span style={{ fontSize: 16, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mark.name}</span>
            {mark.isDemo && <span style={{ fontSize: 11, fontWeight: 700, color: T.info, background: T.infoSoft, borderRadius: RADIUS.pill, padding: '1px 7px', flexShrink: 0 }}>Demo</span>}
          </span>
          <span style={{ display: 'block', fontSize: 12.5, color: next ? T.textSub : T.textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {[nextHuntLine(next), role].filter(Boolean).join(' · ')}
          </span>
        </span>
        {images.length > 0 && (
          <span aria-hidden style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            {images.slice(0, maxImages).map(img => (
              <img key={img.id} src={resolveUrl(img.imageUrl)} alt="" loading="lazy"
                style={{ width: 34, height: 34, borderRadius: RADIUS.sm, objectFit: 'cover', background: T.surface2 }}
                onError={e => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }} />
            ))}
          </span>
        )}
      </button>
      <button type="button" onClick={onToggleHome} aria-pressed={isHome} aria-label={isHome ? `Ta bort ${mark.name} som hemmamark` : `Sätt ${mark.name} som hemmamark`}
        title={isHome ? 'Hemmamark' : 'Sätt som hemmamark'} style={{
          width: 40, height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'none', border: 'none', cursor: 'pointer', color: isHome ? T.accentText : T.textMuted, opacity: isHome ? 1 : 0.55,
        }}>
        <Icon as={Home} size={18} />
      </button>
    </div>
  );
}
